import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import {
  Editor,
  EditorProvider,
  Toolbar,
  BtnUndo,
  BtnRedo,
  Separator,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnStrikeThrough,
  BtnNumberedList,
  BtnBulletList,
  BtnLink,
  BtnClearFormatting,
  BtnStyles,
  HtmlButton,
} from 'react-simple-wysiwyg';

const MAX_IMAGE_URL_LENGTH = 2048;
const MAX_IMAGE_WIDTH = 600;
const MIN_IMAGE_WIDTH = 80;
const ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.avif'];
const IMAGE_PRESETS = {
  small: 180,
  medium: 320,
  large: 520,
};
const IMAGE_ALIGNMENT_VALUES = ['left', 'center', 'right'];

const defaultImageDialogState = {
  open: false,
  mode: 'insert',
  imageUrl: '',
  altText: '',
  width: IMAGE_PRESETS.medium,
  preset: 'medium',
  alignment: 'left',
  validationError: '',
};

/** Rich text editor toolbar matching react-simple-wysiwyg default. */
export function CustomizationRichTextEditor({
  value,
  onChange,
  placeholder,
  enableImageTools = false,
  enableSourceEditor = true,
  enableStyles = true,
  enableStrikeThrough = true,
  sanitizeHtml,
  ariaLabel,
}) {
  const editorRootRef = useRef(null);
  const selectedImageRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [imageDialog, setImageDialog] = useState(defaultImageDialogState);

  const getEditorElement = () => editorRootRef.current?.querySelector('[contenteditable="true"]') || null;

  const handleSafeChange = (event) => {
    const html = sanitizeHtml(event.target.value);
    const editor = getEditorElement();
    if (editor && editor.innerHTML !== html) editor.innerHTML = html;
    onChange?.({ target: { value: html } });
  };

  const insertSafeHtml = (html) => {
    const editor = getEditorElement();
    if (!editor) return;
    const safeHtml = sanitizeHtml(html);
    const template = document.createElement('template');
    template.innerHTML = safeHtml;
    const selection = window.getSelection();
    if (selection?.rangeCount && editor.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const last = template.content.lastChild;
      range.insertNode(template.content);
      if (last) {
        range.setStartAfter(last);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      editor.appendChild(template.content);
    }
    handleSafeChange({ target: { value: editor.innerHTML } });
  };

  const handleSafeTransfer = (event) => {
    if (!sanitizeHtml) return;
    event.preventDefault();
    const data = event.clipboardData || event.dataTransfer;
    const html = data?.getData('text/html');
    const plain = document.createElement('div');
    plain.textContent = data?.getData('text/plain') || '';
    insertSafeHtml(html || plain.innerHTML);
  };

  const clampImageWidth = (width) => {
    const num = Number(width);
    if (!Number.isFinite(num)) return IMAGE_PRESETS.medium;
    return Math.min(MAX_IMAGE_WIDTH, Math.max(MIN_IMAGE_WIDTH, Math.round(num)));
  };

  const getPresetFromWidth = (width) => {
    const clamped = clampImageWidth(width);
    const preset = Object.entries(IMAGE_PRESETS).find(([, presetWidth]) => presetWidth === clamped);
    return preset ? preset[0] : 'custom';
  };

  const getImageStyle = (width, alignment) => {
    const safeAlignment = IMAGE_ALIGNMENT_VALUES.includes(alignment) ? alignment : 'left';
    const clampedWidth = clampImageWidth(width);
    const marginMap = {
      left: 'margin: 8px auto 8px 0;',
      center: 'margin: 8px auto;',
      right: 'margin: 8px 0 8px auto;',
    };
    return `width: ${clampedWidth}px; max-width: 100%; height: auto; display: block; ${marginMap[safeAlignment]}`;
  };

  const getImageUrlValidationError = (rawUrl) => {
    const imageUrl = (rawUrl || '').trim();
    if (!imageUrl) return 'Image URL is required.';
    if (imageUrl.length > MAX_IMAGE_URL_LENGTH) {
      return `Image URL is too long (max ${MAX_IMAGE_URL_LENGTH} characters).`;
    }
    const hasControlChars = [...imageUrl].some((ch) => {
      const code = ch.charCodeAt(0);
      return code <= 31 || code === 127;
    });
    if (/\s/.test(imageUrl) || hasControlChars) {
      return 'Image URL cannot contain spaces or control characters.';
    }

    let parsed;
    try {
      parsed = new URL(imageUrl);
    } catch (_err) {
      return 'Use a valid URL.';
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'Use a valid http:// or https:// URL.';
    }
    if (parsed.username || parsed.password) {
      return 'Image URL cannot include embedded username or password.';
    }

    const pathname = (parsed.pathname || '').toLowerCase();
    const hasAllowedExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext));
    if (!hasAllowedExtension) {
      return `Image URL must end with one of: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}.`;
    }

    return '';
  };

  const isValidImageUrl = (url) => !getImageUrlValidationError(url);

  const emitHtmlChange = (nextHtml) => {
    if (!onChange) return;
    onChange({ target: { value: nextHtml } });
  };

  const syncHtmlFromEditor = () => {
    const editorElement = getEditorElement();
    if (!editorElement) return;
    emitHtmlChange(editorElement.innerHTML || '');
  };

  const clearImageDialog = () => {
    setImageDialog(defaultImageDialogState);
    selectedImageRef.current = null;
  };

  const captureSelection = () => {
    const editorElement = getEditorElement();
    if (!editorElement) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount < 1) return;
    const range = selection.getRangeAt(0);
    if (!editorElement.contains(range.commonAncestorContainer)) return;
    savedRangeRef.current = range.cloneRange();
  };

  const restoreSelection = () => {
    if (!savedRangeRef.current) return;
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(savedRangeRef.current);
  };

  const buildSafeImageHtml = ({ src, alt, width, alignment }) => {
    const img = document.createElement('img');
    img.setAttribute('src', src);
    img.setAttribute('alt', (alt || '').trim());
    img.setAttribute('width', String(width));
    img.setAttribute('data-align', alignment);
    img.setAttribute('style', getImageStyle(width, alignment));
    return img.outerHTML;
  };

  const openImageDialogForInsert = () => {
    captureSelection();
    selectedImageRef.current = null;
    setImageDialog({ ...defaultImageDialogState, open: true, mode: 'insert' });
  };

  const applyImageChanges = () => {
    const editorElement = getEditorElement();
    const imageUrl = imageDialog.imageUrl.trim();
    const urlValidationError = getImageUrlValidationError(imageUrl);
    if (urlValidationError) {
      setImageDialog((current) => ({ ...current, validationError: urlValidationError }));
      return;
    }

    const width = clampImageWidth(imageDialog.width);
    const alignment = IMAGE_ALIGNMENT_VALUES.includes(imageDialog.alignment) ? imageDialog.alignment : 'left';

    if (imageDialog.mode === 'edit' && selectedImageRef.current) {
      const selectedImage = selectedImageRef.current;
      selectedImage.setAttribute('src', imageUrl);
      selectedImage.setAttribute('alt', (imageDialog.altText || '').trim());
      selectedImage.setAttribute('width', String(width));
      selectedImage.setAttribute('data-align', alignment);
      selectedImage.setAttribute('style', getImageStyle(width, alignment));
      syncHtmlFromEditor();
      clearImageDialog();
      return;
    }

    if (!editorElement) {
      setImageDialog((current) => ({ ...current, validationError: 'Editor is not ready yet. Please try again.' }));
      return;
    }

    editorElement.focus();
    restoreSelection();

    const imgHtml = buildSafeImageHtml({
      src: imageUrl,
      alt: imageDialog.altText,
      width,
      alignment,
    });

    const inserted = document.execCommand('insertHTML', false, imgHtml);
    if (!inserted) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const wrapper = document.createElement('div');
        wrapper.innerHTML = imgHtml;
        const fragment = document.createDocumentFragment();
        while (wrapper.firstChild) {
          fragment.appendChild(wrapper.firstChild);
        }
        range.deleteContents();
        range.insertNode(fragment);
      } else {
        editorElement.innerHTML += imgHtml;
      }
    }

    syncHtmlFromEditor();
    clearImageDialog();
  };

  useEffect(() => {
    const editorElement = getEditorElement();
    if (!enableImageTools || !editorElement) return undefined;

    const onEditorClick = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement)) return;
      const styleWidth = target.style?.width ? parseInt(target.style.width, 10) : NaN;
      const attrWidth = parseInt(target.getAttribute('width'), 10);
      const width = Number.isFinite(styleWidth)
        ? clampImageWidth(styleWidth)
        : (Number.isFinite(attrWidth) ? clampImageWidth(attrWidth) : IMAGE_PRESETS.medium);
      const storedAlignment = target.getAttribute('data-align');
      const alignment = IMAGE_ALIGNMENT_VALUES.includes(storedAlignment) ? storedAlignment : 'left';

      selectedImageRef.current = target;
      setImageDialog({
        open: true,
        mode: 'edit',
        imageUrl: target.getAttribute('src') || '',
        altText: target.getAttribute('alt') || '',
        width,
        preset: getPresetFromWidth(width),
        alignment,
        validationError: '',
      });
    };

    editorElement.addEventListener('click', onEditorClick);
    return () => {
      editorElement.removeEventListener('click', onEditorClick);
    };
    // Helper functions used here depend on refs/state setters and do not need identity-based rebinds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableImageTools, value]);

  return (
    <>
      <Box
        ref={editorRootRef}
        onPasteCapture={sanitizeHtml ? handleSafeTransfer : undefined}
        onDropCapture={sanitizeHtml ? handleSafeTransfer : undefined}
      >
        <EditorProvider>
          <Editor
            value={sanitizeHtml ? sanitizeHtml(value) : value}
            onChange={sanitizeHtml ? handleSafeChange : onChange}
            aria-label={ariaLabel}
            role={ariaLabel ? 'textbox' : undefined}
            aria-multiline={ariaLabel ? true : undefined}
            placeholder={placeholder}
            containerProps={{
              style: {
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                minHeight: 120,
                boxSizing: 'border-box',
              },
            }}
          >
            <Toolbar
              style={{
                flexWrap: 'wrap',
                maxWidth: '100%',
                rowGap: 4,
                boxSizing: 'border-box',
              }}
            >
              <BtnUndo />
              <BtnRedo />
              <Separator />
              <BtnBold />
              <BtnItalic />
              <BtnUnderline />
              {enableStrikeThrough && <BtnStrikeThrough />}
              <Separator />
              <BtnNumberedList />
              <BtnBulletList />
              <Separator />
              {sanitizeHtml ? (
                <button
                  type="button"
                  className="rsw-btn"
                  title="Link"
                  onMouseDown={(event) => { event.preventDefault(); captureSelection(); }}
                  onClick={() => {
                    const href = window.prompt('Link URL (http:// or https://)');
                    if (!href) return;
                    const link = document.createElement('a');
                    link.setAttribute('href', href);
                    link.textContent = window.getSelection()?.toString() || href;
                    restoreSelection();
                    insertSafeHtml(link.outerHTML);
                  }}
                >🔗</button>
              ) : <BtnLink />}
              <BtnClearFormatting />
              {enableSourceEditor && <HtmlButton />}
              {enableStyles && <><Separator /><BtnStyles /></>}
              {enableImageTools && (
                <>
                  <Separator />
                  <button
                    type="button"
                    className="rsw-btn"
                    title="Insert image by URL"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      captureSelection();
                    }}
                    onClick={openImageDialogForInsert}
                    style={{ minWidth: 32 }}
                  >
                    <ImageIcon fontSize="small" />
                  </button>
                </>
              )}
            </Toolbar>
          </Editor>
        </EditorProvider>
      </Box>

      {enableImageTools && (
        <Dialog open={imageDialog.open} onClose={clearImageDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{imageDialog.mode === 'edit' ? 'Edit image' : 'Insert image'}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
              <TextField
                label="Image URL"
                value={imageDialog.imageUrl}
                onChange={(event) => setImageDialog((current) => ({
                  ...current,
                  imageUrl: event.target.value,
                  validationError: '',
                }))}
                placeholder="https://example.com/logo.png"
                fullWidth
                size="small"
                helperText={`Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')} (${MAX_IMAGE_URL_LENGTH} char max)`}
              />
              <TextField
                label="Alt text (optional)"
                value={imageDialog.altText}
                onChange={(event) => setImageDialog((current) => ({
                  ...current,
                  altText: event.target.value,
                  validationError: '',
                }))}
                fullWidth
                size="small"
              />
              <TextField
                select
                label="Size preset"
                value={imageDialog.preset}
                onChange={(event) => {
                  const preset = event.target.value;
                  if (preset === 'custom') {
                    setImageDialog((current) => ({ ...current, preset, validationError: '' }));
                    return;
                  }
                  const presetWidth = IMAGE_PRESETS[preset] || IMAGE_PRESETS.medium;
                  setImageDialog((current) => ({
                    ...current,
                    preset,
                    width: presetWidth,
                    validationError: '',
                  }));
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </TextField>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Image width: {clampImageWidth(imageDialog.width)}px
                </Typography>
                <Slider
                  value={clampImageWidth(imageDialog.width)}
                  min={MIN_IMAGE_WIDTH}
                  max={MAX_IMAGE_WIDTH}
                  step={10}
                  onChange={(_event, sliderWidth) => {
                    const width = clampImageWidth(sliderWidth);
                    setImageDialog((current) => ({
                      ...current,
                      width,
                      preset: getPresetFromWidth(width),
                      validationError: '',
                    }));
                  }}
                  valueLabelDisplay="auto"
                />
              </Box>

              <TextField
                select
                label="Alignment"
                value={imageDialog.alignment}
                onChange={(event) => setImageDialog((current) => ({
                  ...current,
                  alignment: event.target.value,
                  validationError: '',
                }))}
                fullWidth
                size="small"
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </TextField>

              {imageDialog.validationError && (
                <Typography color="error" variant="body2">{imageDialog.validationError}</Typography>
              )}

              {imageDialog.imageUrl?.trim() && isValidImageUrl(imageDialog.imageUrl.trim()) && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    Preview (external image)
                  </Typography>
                  <Box sx={{ minHeight: 36 }}>
                    <img
                      src={imageDialog.imageUrl.trim()}
                      alt={imageDialog.altText || 'Preview'}
                      width={clampImageWidth(imageDialog.width)}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        display: 'block',
                        margin: imageDialog.alignment === 'center'
                          ? '8px auto'
                          : (imageDialog.alignment === 'right' ? '8px 0 8px auto' : '8px auto 8px 0'),
                      }}
                    />
                  </Box>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                Remote images may be blocked by some clients until recipients choose to display images.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={clearImageDialog}>Cancel</Button>
            <Button variant="contained" onClick={applyImageChanges}>
              {imageDialog.mode === 'edit' ? 'Apply changes' : 'Insert image'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export const wysiwygEditorBoxSx = {
  mb: 2,
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 200,
  '& .rsw-editor': {
    maxWidth: '100%',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
  },
  '& .rsw-toolbar, & .r-s-wysiwyg-toolbar': {
    marginBottom: 1,
    flex: '0 0 auto',
    maxWidth: '100%',
    minWidth: 0,
  },
  '& .rsw-editor, & .rsw-content, & [contenteditable="true"]': {
    flex: '1 1 auto',
    minHeight: 120,
    maxHeight: 360,
    overflow: 'auto',
    padding: '8px 12px',
    boxSizing: 'border-box',
    margin: 0,
    maxWidth: '100%',
  },
  '& .rsw-dd': {
    maxWidth: 'min(100%, 160px)',
  },
  '& textarea': {
    flex: '1 1 auto',
    minHeight: 120,
    maxHeight: 360,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'auto',
    fontFamily: 'monospace',
    padding: '8px 12px',
    boxSizing: 'border-box',
    margin: 0,
    lineHeight: 1.4,
  },
};
